import { createLogger } from '@/utils/logger';
import { reloadOncePerBuild } from '@/utils/reloadOncePerBuild.js';

const logger = createLogger('lazyRoute');

/**
 * Detects whether an import error is a chunk load failure (stale deploy)
 * vs. a transient network error worth retrying.
 */
function isChunkLoadError(error) {
  const msg = error?.message || '';
  return (
    error?.name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  );
}

/**
 * Retries a dynamic import() on failure with a delay between attempts.
 * Handles transient failures like Vite dev server still compiling a chunk,
 * network hiccups, or stale chunks after a deployment.
 *
 * If retries are exhausted and the error looks like a stale chunk (post-deploy),
 * auto-reloads the page so the browser fetches a fresh index.html with
 * updated chunk references, at most once per build (reloadOncePerBuild).
 *
 * @param {Function} importFn - Dynamic import function (e.g., () => import('@/component/...'))
 * @param {number} retries - Number of retry attempts remaining
 * @param {number} delay - Milliseconds to wait between retries
 * @returns {Promise} Resolves with the module or rejects after all retries exhausted
 */
export async function importWithRetry(importFn, retries = 3, delay = 1000) {
  try {
    return await importFn();
  } catch (error) {
    if (retries <= 0) {
      // Retries exhausted: a stale chunk from a deploy reloads to pick up the
      // new index.html and chunk manifest, once per build; otherwise the
      // error boundary handles it.
      if (isChunkLoadError(error) && reloadOncePerBuild()) {
        // Never resolves, so the UI doesn't flash an error boundary while the reload is in progress.
        return new Promise(() => {});
      }
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
    return importWithRetry(importFn, retries - 1, delay);
  }
}

/**
 * Creates a lazy route loader for React Router.
 * Transforms the default export to a Component property as React Router expects.
 * Automatically retries failed chunk loads before surfacing errors.
 *
 * If all retries fail, does a hard page reload instead of letting the error
 * bubble to the error boundary. This handles stale chunks after deploys and
 * Vite HMR invalidation after dev server restarts — the user sees a clean
 * page load instead of a ghost mascot error screen.
 *
 * React Router's lazy() expects: { Component: ReactComponent }
 * ES modules export: { default: ReactComponent }
 *
 * Usage:
 *   {
 *     path: 'my-route',
 *     lazy: lazyRoute(() => import('@/component/pages/my-page')),
 *   }
 *
 * @param {Function} importFn - Dynamic import function returning the component module
 * @returns {Function} Async function that returns { Component }
 */
export function lazyRoute(importFn) {
  return async () => {
    try {
      const module = await importWithRetry(importFn);
      return { Component: module.default };
    } catch (err) {
      // All retries exhausted. Hard reload to get fresh modules, once per
      // build: avoids flashing the error boundary while React Router aborts
      // the navigation, and never loops on a build that cannot load it.
      if (reloadOncePerBuild()) {
        logger.warn({ err }, 'route chunk failed after retries; reloading for a newer build');
        return new Promise(() => {});
      }
      throw new Error('Failed to load page after reload. Please refresh manually.', { cause: err });
    }
  };
}
